/**
 * InteractiveFeatureCards - 3 principle cards with scroll-to Deep Dive section
 * Click a card to scroll down and reveal detailed explanation
 */

import { useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Target, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DsosIcon } from '@/components/ui/dsos-icon';
import { DecisionEngineIllustration } from '@/components/illustrations/DecisionEngineIllustration';
import { PrincipleSavesTimeIllustration } from '@/components/landing/illustrations/PrincipleSavesTimeIllustration';
import { PrincipleInactionIllustration } from '@/components/landing/illustrations/PrincipleInactionIllustration';

type PrincipleId = 'decides' | 'removes' | 'inaction';

interface PrincipleContent {
  title: string;
  subtitle: string;
  body: string[];
  bullets: string[];
  takeaway: {
    you: string;
    dsos: string;
  };
}

const principles: Record<PrincipleId, PrincipleContent> = {
  decides: {
    title: 'It decides what matters.',
    subtitle: 'The system removes the need to choose priorities.',
    body: [
      'Traditional sales workflows force you to make dozens of decisions every morning: Which lead should I call first? Which deal needs attention? What\'s the most urgent follow-up?',
      'Decision Sales OS eliminates this cognitive load by calculating priorities automatically. Every morning, you see exactly what needs attention based on three factors: revenue impact, time decay, and execution risk.',
      'The system evaluates your entire pipeline continuously, weighing the cost of delay against the potential revenue at stake. It doesn\'t just show you data—it tells you what to do about it.',
      'This means no more spending the first hour of your day deciding what to work on. The system has already decided. You execute.',
    ],
    bullets: [
      'Priorities calculated automatically using revenue impact, time decay, and execution risk',
      'Clear actions presented every morning—no interpretation required',
      'Continuous evaluation of your entire pipeline',
      'Eliminates the cognitive load of choosing what to work on',
    ],
    takeaway: {
      you: 'You execute the actions that matter most.',
      dsos: 'DSOS decides what matters and presents clear priorities.',
    },
  },
  removes: {
    title: 'It saves time by removing choices.',
    subtitle: 'Execution is faster because decisions are reduced.',
    body: [
      'Every choice you make takes time. The more decisions you face, the slower your execution becomes. This is especially true in sales, where analysis paralysis can kill momentum.',
      'Decision Sales OS removes the need to choose by presenting only the actions that matter right now. Instead of seeing a long list of leads and deals, you see a prioritized sequence of actions.',
      'The system eliminates "what should I do next?" moments. There\'s no debate, no comparison, no weighing options. Just the next action, clearly defined.',
      'This reduction in decision-making translates directly to time saved. Less thinking, more doing. Faster follow-ups. More deals closed.',
    ],
    bullets: [
      'Presents only the actions that matter right now',
      'Eliminates "what should I do next?" moments',
      'No debate, no comparison, no weighing options',
      'Faster follow-ups and more deals closed',
    ],
    takeaway: {
      you: 'You execute faster with fewer decisions.',
      dsos: 'DSOS removes choices and presents the next action.',
    },
  },
  inaction: {
    title: 'It makes inaction visible.',
    subtitle: 'Missed actions are surfaced before revenue is lost.',
    body: [
      'In traditional CRMs, missed follow-ups and delayed actions are invisible until it\'s too late. By the time you realize a deal has gone cold, the opportunity is already lost.',
      'Decision Sales OS makes inaction visible by quantifying the cost of delay. You see exactly how much revenue is at risk if you don\'t act today. Every missed action has a price tag.',
      'The system surfaces missed actions before they become lost revenue. It shows you not just what you should do, but what happens if you don\'t. This visibility creates urgency and accountability.',
      'When inaction is visible, it becomes preventable. You can\'t ignore what you can see. The system ensures that critical actions don\'t slip through the cracks.',
    ],
    bullets: [
      'Quantifies the cost of delay for every action',
      'Shows exactly how much revenue is at risk',
      'Surfaces missed actions before they become lost revenue',
      'Creates visibility that prevents inaction',
    ],
    takeaway: {
      you: 'You see the cost of inaction before it\'s too late.',
      dsos: 'DSOS makes inaction visible and quantifies the risk.',
    },
  },
};

const features = [
  {
    id: 'decides' as PrincipleId,
    icon: Target,
    title: 'It decides what matters.',
    description: 'The system removes the need to choose priorities.',
  },
  {
    id: 'removes' as PrincipleId,
    icon: Clock,
    title: 'It saves time by removing choices.',
    description: 'Execution is faster because decisions are reduced.',
  },
  {
    id: 'inaction' as PrincipleId,
    icon: AlertTriangle,
    title: 'It makes inaction visible.',
    description: 'Missed actions are surfaced before revenue is lost.',
  },
];

export function InteractiveFeatureCards() {
  const [activePrinciple, setActivePrinciple] = useState<PrincipleId>('decides');
  const deepDiveRef = useRef<HTMLDivElement>(null);
  const shouldReduceMotion = useReducedMotion();

  const handleCardClick = (principleId: PrincipleId) => {
    setActivePrinciple(principleId);
    
    // Smooth scroll to Deep Dive section with offset for sticky header
    setTimeout(() => {
      if (deepDiveRef.current) {
        const headerOffset = 96; // Adjust based on your sticky header height
        const elementPosition = deepDiveRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    }, 100);
  };

  const activeContent = principles[activePrinciple];

  return (
    <>
      {/* Principle Cards */}
      <div className="grid md:grid-cols-3 gap-[var(--gap-comfortable)] md:gap-[var(--gap-spacious)] mb-24 md:mb-32">
        {features.map((feature) => {
          const isActive = activePrinciple === feature.id;
          const Icon = feature.icon;

          return (
            <motion.button
              key={feature.id}
              type="button"
              onClick={() => handleCardClick(feature.id)}
              className={cn(
                'w-full p-6 md:p-8 rounded-[20px] border bg-card',
                'h-full flex flex-col text-left transition-all relative overflow-hidden',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                isActive
                  ? 'border-primary/40 bg-gradient-to-br from-primary/[0.08] to-primary/[0.02] shadow-lg'
                  : 'border-border/40 hover:border-border/60 hover:bg-accent/5'
              )}
              whileHover={{ y: -4 }}
              whileTap={{ scale: 0.98 }}
              transition={{
                duration: 0.18,
                ease: [0.16, 1, 0.3, 1],
              }}
            >
              {/* Subtle active indicator dot */}
              {isActive && (
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="absolute top-4 right-4 w-2 h-2 rounded-full bg-primary z-10"
                />
              )}

              {/* Subtle glow for active state */}
              {isActive && (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
              )}

              {/* Icon */}
              <div className={cn(
                'p-2 rounded-lg bg-accent/10 border border-border/40 w-fit mb-4 relative z-10 transition-colors',
                isActive && 'bg-primary/20 border-primary/30'
              )}>
                <DsosIcon 
                  icon={Icon} 
                  size="md" 
                  className={isActive ? 'text-primary' : 'text-foreground'} 
                />
              </div>

              {/* Title */}
              <h3 className={cn(
                'text-lg md:text-xl font-semibold mb-3 tracking-tight leading-tight relative z-10',
                isActive ? 'text-foreground' : 'text-foreground'
              )}>
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed flex-grow relative z-10">
                {feature.description}
              </p>
            </motion.button>
          );
        })}
      </div>

      {/* Deep Dive Section */}
      <div
        ref={deepDiveRef}
        className="scroll-mt-24"
        style={{ scrollMarginTop: '96px' }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={activePrinciple}
            initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 20 }}
            animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
            exit={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: -20 }}
            transition={{
              duration: shouldReduceMotion ? 0.2 : 0.4,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="space-y-8 md:space-y-12"
          >
            {/* Header */}
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-3 leading-[var(--leading-headline)]">
                {activeContent.title}
              </h2>
              <p className="text-[var(--font-body-lg)] text-muted-foreground max-w-2xl mx-auto leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                {activeContent.subtitle}
              </p>
            </div>

            {/* Content Grid: Illustration + Text */}
            <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-start">
              <div className="aspect-video w-full rounded-2xl border bg-card shadow-sm p-6 md:p-8 overflow-hidden">
                {activePrinciple === 'decides' && <DecisionEngineIllustration className="h-full w-full" />}
                {activePrinciple === 'removes' && <PrincipleSavesTimeIllustration className="h-full w-full" />}
                {activePrinciple === 'inaction' && <PrincipleInactionIllustration className="h-full w-full" />}
              </div>

              {/* Text Content */}
              <div className="space-y-6">
                {/* Body paragraphs */}
                <div className="space-y-4">
                  {activeContent.body.map((paragraph, index) => (
                    <motion.p
                      key={index}
                      initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
                      animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                      transition={{ duration: shouldReduceMotion ? 0.2 : 0.4, delay: shouldReduceMotion ? 0 : 0.2 + index * 0.1 }}
                      className="text-[var(--font-body)] text-foreground/90 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]"
                    >
                      {paragraph}
                    </motion.p>
                  ))}
                </div>

                {/* Bullet list */}
                <motion.div
                  initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
                  animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  transition={{ duration: shouldReduceMotion ? 0.2 : 0.4, delay: shouldReduceMotion ? 0 : 0.5 }}
                  className="space-y-3 pt-4 border-t border-border/40"
                >
                  <h4 className="text-sm font-semibold text-foreground mb-3">Key points:</h4>
                  <ul className="space-y-2.5">
                    {activeContent.bullets.map((bullet, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-muted-foreground leading-relaxed">{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Takeaway comparison */}
                <motion.div
                  initial={shouldReduceMotion ? { opacity: 0 } : { opacity: 0, y: 12 }}
                  animate={shouldReduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                  transition={{ duration: shouldReduceMotion ? 0.2 : 0.4, delay: shouldReduceMotion ? 0 : 0.6 }}
                  className="pt-4 border-t border-border/40"
                >
                  <div className="grid md:grid-cols-2 gap-4 p-4 rounded-lg bg-accent/5 border border-border/40">
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">What you do</p>
                      <p className="text-sm text-foreground leading-relaxed">{activeContent.takeaway.you}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">What DSOS does</p>
                      <p className="text-sm text-foreground leading-relaxed">{activeContent.takeaway.dsos}</p>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

            {/* Optional subtle link - removed dead button */}
          </motion.div>
        </AnimatePresence>
      </div>
    </>
  );
}
